import { motion } from 'framer-motion';
import { type ButtonHTMLAttributes, forwardRef, type Ref } from 'react';
import { cn } from '@/lib/cn';

type Variant = 'primary' | 'ghost' | 'danger' | 'accent';
type Size = 'sm' | 'md' | 'lg' | 'xl';

interface Props extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  size?: Size;
  fullWidth?: boolean;
  /**
   * When true, the button performs a gentle idle wiggle every ~3s
   * to draw the eye. Use on the "obvious next action" — Next button
   * between rounds, Submit once the player has picked, Start on the
   * lobby. Default off; explicit so we don't have every button
   * wagging at once.
   */
  pulse?: boolean;
  /**
   * Tags this button with `data-fit-anchor="primary-cta"` so the
   * fluid-snap harness asserts it stays inside the viewport at
   * every device size. Use on the screen's primary action.
   */
  primaryCta?: boolean;
}

export const Button = forwardRef(function Button(
  {
    variant = 'primary',
    size = 'md',
    fullWidth,
    pulse,
    primaryCta,
    className,
    children,
    disabled,
    ...rest
  }: Props,
  ref: Ref<HTMLButtonElement>,
) {
  // When the button is `disabled` the HTML attribute already blocks
  // clicks — but the visuals were identical to the enabled state, so
  // an invalid form looked ready-to-submit. We:
  //   1. Apply Tailwind `disabled:` utility variants for opacity +
  //      desaturation + flat shadow + not-allowed cursor.
  //   2. Skip framer-motion's hover/tap/pulse animations so the
  //      button doesn't dance under the cursor while inert.
  return (
    <motion.button
      ref={ref}
      disabled={disabled}
      whileTap={disabled ? undefined : { scale: 0.94, y: 3, rotate: 0 }}
      whileHover={disabled ? undefined : { y: -2, scale: 1.03, rotate: -0.5 }}
      animate={
        pulse && !disabled
          ? {
              rotate: [0, -2.5, 2.5, -1.5, 1.5, 0],
              scale: [1, 1.04, 1, 1.02, 1],
            }
          : undefined
      }
      transition={
        pulse && !disabled
          ? {
              duration: 1.6,
              repeat: Number.POSITIVE_INFINITY,
              repeatDelay: 2.2,
              ease: 'easeInOut',
            }
          : { type: 'spring', stiffness: 600, damping: 20 }
      }
      data-fit-anchor={primaryCta ? 'primary-cta' : undefined}
      className={cn(
        'ink-border rounded-chunk shadow-chunk display font-display font-bold',
        'inline-flex items-center justify-center gap-2 select-none',
        'active:shadow-chunk-press',
        'disabled:opacity-50 disabled:saturate-50 disabled:shadow-none',
        'disabled:cursor-not-allowed disabled:active:shadow-none',
        fullWidth && 'w-full',
        size === 'sm' && 'px-3 py-1.5 text-sm',
        size === 'md' && 'px-5 py-2.5 text-base',
        size === 'lg' && 'px-7 py-3.5 text-xl',
        size === 'xl' && 'px-9 py-5 text-2xl md:text-3xl',
        variant === 'primary' && 'bg-paper text-ink',
        variant === 'ghost' && 'bg-canvas text-ink',
        variant === 'accent' && 'bg-accent text-paper',
        variant === 'danger' && 'bg-a1 text-a1-ink',
        className,
      )}
      {...(rest as Record<string, unknown>)}
    >
      {children}
    </motion.button>
  );
});
